import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '@lib/supabase/client';

interface PresenceData {
  user_id: string;
  email: string;
  last_seen_at: string;
  current_page?: string;
  is_online: boolean;
}

// Consider user online if last seen within 2 minutes
const ONLINE_THRESHOLD_MS = 2 * 60 * 1000;
const UPDATE_INTERVAL_MS = 60 * 1000; // Update every 60 seconds (was 30)
const IDLE_TIMEOUT_MS = 90 * 1000; // Mark as offline after 90 seconds of inactivity (was 45)
const ACTIVITY_THROTTLE_MS = 10 * 1000; // Throttle activity updates to every 10 seconds (was 5)

export const usePresenceTracker = () => {
  const [isTracking, setIsTracking] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPageVisibleRef = useRef(true);
  const isPageFocusedRef = useRef(true);
  const isUserActiveRef = useRef(true);
  const lastActivityRef = useRef(Date.now());
  const lastActivityUpdateRef = useRef(0);
  const isInitializedRef = useRef(false);

  const markAsOffline = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      await supabase
        .from('user_presence')
        .update({ is_online: false, last_seen_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('presence_date', today);
      
      isUserActiveRef.current = false;
    } catch (error) {
      console.error('Error marking as offline:', error);
    }
  }, []);

  const updatePresence = useCallback(async (forceOnline = false) => {
    try {
      // Only update if:
      // 1. Force update, OR
      // 2. Page is visible AND focused AND user has been active recently
      const isReallyActive = isPageVisibleRef.current && isPageFocusedRef.current && isUserActiveRef.current;
      
      if (!forceOnline && !isReallyActive) {
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) return;

      // Get today's date in YYYY-MM-DD format
      const today = new Date().toISOString().split('T')[0];

      const presenceData = {
        user_id: user.id,
        email: user.email.toLowerCase(),
        last_seen_at: new Date().toISOString(),
        current_page: window.location.pathname,
        is_online: true,
        presence_date: today,
      };

      // Upsert presence record - now using user_id + presence_date as unique key
      const { error } = await supabase
        .from('user_presence')
        .upsert(presenceData, { 
          onConflict: 'user_id,presence_date',
          ignoreDuplicates: false 
        });

      if (error) {
        console.error('Error updating presence:', error);
      } else {
        setIsTracking(true);
        isUserActiveRef.current = true;
      }
    } catch (error) {
      console.error('Presence update error:', error);
    }
  }, []);

  const resetIdleTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    
    // Throttle activity updates to avoid too many DB calls
    const shouldUpdate = now - lastActivityUpdateRef.current > ACTIVITY_THROTTLE_MS;
    
    // If user was marked as inactive, mark them active again
    if (!isUserActiveRef.current && isPageVisibleRef.current && isPageFocusedRef.current) {
      isUserActiveRef.current = true;
      if (shouldUpdate) {
        lastActivityUpdateRef.current = now;
        updatePresence(true);
      }
    }

    // Clear existing idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    // Set new idle timeout - mark as offline after inactivity
    idleTimeoutRef.current = setTimeout(() => {
      // User is idle - mark as offline
      isUserActiveRef.current = false;
      markAsOffline();
    }, IDLE_TIMEOUT_MS);
  }, [updatePresence, markAsOffline]);

  useEffect(() => {
    // Initialize refs safely on first render (client-side only)
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      isPageVisibleRef.current = typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
      isPageFocusedRef.current = typeof document !== 'undefined' ? document.hasFocus() : true;
    }
    
    // Only start tracking if page is visible and focused
    if (isPageVisibleRef.current && isPageFocusedRef.current) {
      updatePresence(true);
      resetIdleTimer();
    }

    // Set up interval for periodic updates (only when truly active)
    intervalRef.current = setInterval(() => {
      const isReallyActive = isPageVisibleRef.current && isPageFocusedRef.current && isUserActiveRef.current;
      if (isReallyActive) {
        updatePresence();
      }
    }, UPDATE_INTERVAL_MS);

    // Handle page visibility change (tab switching)
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      isPageVisibleRef.current = isVisible;
      
      if (isVisible && document.hasFocus()) {
        // User returned to the page AND it's focused
        isPageFocusedRef.current = true;
        isUserActiveRef.current = true;
        updatePresence(true);
        resetIdleTimer();
      } else {
        // User left the page (switched tab/minimized)
        isUserActiveRef.current = false;
        markAsOffline();
      }
    };

    // Handle user activity events with heavy throttling to prevent lag
    let lastActivityTime = 0;
    const ACTIVITY_DEBOUNCE_MS = 5000; // Only process activity every 5 seconds (was 2)
    
    const handleUserActivity = () => {
      const now = Date.now();
      // Heavy throttle - only process every 2 seconds max
      if (now - lastActivityTime < ACTIVITY_DEBOUNCE_MS) return;
      lastActivityTime = now;
      
      // Only track activity if page is truly active
      if (isPageVisibleRef.current && isPageFocusedRef.current) {
        resetIdleTimer();
      }
    };

    // Handle window focus (more reliable than visibility for detecting active tab)
    const handleFocus = () => {
      isPageFocusedRef.current = true;
      isPageVisibleRef.current = true;
      isUserActiveRef.current = true;
      updatePresence(true);
      resetIdleTimer();
    };

    // Handle window blur (user clicked outside or switched to another window)
    const handleBlur = () => {
      isPageFocusedRef.current = false;
      // Mark as offline immediately when window loses focus
      isUserActiveRef.current = false;
      markAsOffline();
    };

    // Store session reference for beforeunload
    let currentUserId: string | null = null;
    let isMounted = true;

    supabase.auth.getUser().then(({ data: { user } }) => {
      if (isMounted) currentUserId = user?.id || null;
    });

    // Handle before unload (user closing tab/navigating away)
    const handleBeforeUnload = () => {
      // Use stored user ID instead of async call
      if (currentUserId) {
        const today = new Date().toISOString().split('T')[0];
        const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/user_presence?user_id=eq.${currentUserId}&presence_date=eq.${today}`;
        // Use sendBeacon with proper format
        const blob = new Blob([JSON.stringify({ is_online: false, last_seen_at: new Date().toISOString() })], { type: 'application/json' });
        navigator.sendBeacon?.(url, blob);
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Activity detection events
    document.addEventListener('mousemove', handleUserActivity);
    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('touchstart', handleUserActivity);
    document.addEventListener('scroll', handleUserActivity);

    return () => {
      isMounted = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('mousedown', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('touchstart', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
    };
  }, [updatePresence, resetIdleTimer, markAsOffline]);

  return isTracking;
};

export const useOnlineUsers = () => {
  const [onlineUsers, setOnlineUsers] = useState<Map<string, PresenceData>>(new Map());
  const [todayUsers, setTodayUsers] = useState<PresenceData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchOnlineUsers = useCallback(async () => {
    try {
      // Get all presence records from the last 2 minutes (currently online)
      const cutoffTime = new Date(Date.now() - ONLINE_THRESHOLD_MS).toISOString();
      
      const { data, error } = await supabase
        .from('user_presence')
        .select('*')
        .gte('last_seen_at', cutoffTime)
        .eq('is_online', true);

      if (error) {
        console.error('Error fetching online users:', error);
        return;
      }

      const usersMap = new Map<string, PresenceData>();
      (data || []).forEach((record: PresenceData) => {
        if (record.email) {
          usersMap.set(record.email.toLowerCase(), record);
        }
      });

      setOnlineUsers(usersMap);
    } catch (error) {
      console.error('Error in fetchOnlineUsers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTodayUsers = useCallback(async () => {
    try {
      // Get start of today in ISO format
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStart = today.toISOString();
      
      const { data, error } = await supabase
        .from('user_presence')
        .select('*')
        .gte('last_seen_at', todayStart)
        .order('last_seen_at', { ascending: false });

      if (error) {
        console.error('Error fetching today users:', error);
        return;
      }

      // Filter to get unique users by user_id (keep the most recent record for each user)
      const uniqueUsersMap = new Map<string, typeof data[0]>();
      (data || []).forEach(record => {
        if (!uniqueUsersMap.has(record.user_id)) {
          uniqueUsersMap.set(record.user_id, record);
        }
      });
      
      setTodayUsers(Array.from(uniqueUsersMap.values()));
    } catch (error) {
      console.error('Error in fetchTodayUsers:', error);
    }
  }, []);

  const fetchUsersByDate = useCallback(async (date: Date): Promise<PresenceData[]> => {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      
      const { data, error } = await supabase
        .from('user_presence')
        .select('*')
        .gte('last_seen_at', startOfDay.toISOString())
        .lte('last_seen_at', endOfDay.toISOString())
        .order('last_seen_at', { ascending: false });

      if (error) {
        console.error('Error fetching users by date:', error);
        return [];
      }

      // Filter to get unique users by user_id (keep the most recent record for each user)
      const uniqueUsersMap = new Map<string, typeof data[0]>();
      (data || []).forEach(record => {
        if (!uniqueUsersMap.has(record.user_id)) {
          uniqueUsersMap.set(record.user_id, record);
        }
      });

      return Array.from(uniqueUsersMap.values());
    } catch (error) {
      console.error('Error in fetchUsersByDate:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    // Initial fetch
    fetchOnlineUsers();
    fetchTodayUsers();

    // Refresh online users every 15 seconds
    const onlineInterval = setInterval(fetchOnlineUsers, 15000);
    
    // Refresh today users every minute
    const todayInterval = setInterval(fetchTodayUsers, 60000);

    return () => {
      clearInterval(onlineInterval);
      clearInterval(todayInterval);
    };
  }, [fetchOnlineUsers, fetchTodayUsers]);

  const isUserOnline = (email: string): boolean => {
    const presence = onlineUsers.get(email.toLowerCase());
    if (!presence) return false;
    
    // Check if last seen is within threshold AND marked as online
    const lastSeen = new Date(presence.last_seen_at).getTime();
    return presence.is_online && Date.now() - lastSeen < ONLINE_THRESHOLD_MS;
  };

  const getUserPresence = (email: string): PresenceData | undefined => {
    return onlineUsers.get(email.toLowerCase());
  };

  return { 
    onlineUsers, 
    todayUsers,
    isUserOnline, 
    getUserPresence, 
    onlineCount: onlineUsers.size,
    todayCount: todayUsers.length,
    loading,
    refresh: fetchOnlineUsers,
    refreshToday: fetchTodayUsers,
    fetchUsersByDate
  };
};
